import httpClient from 'services/httpClient';
import { API_ENDPOINTS, ApiResponse } from 'services/api';
export interface YearlyCalendarSchedulePlan {
  msLCMSPlanningID: string;
  planningName: string;
  programType: 'program' | 'modular';
  planningDescription: string;
  planningStart: string;
  planningEnd: string;
  prerequisites: string;
  learningMaterials: string;
  facilitiesRequired: string;
  notes: string;
  userIn: string;
  userUp: string | null;
  dateIn: string;
  dateUp: string | null;
  isDeleted: boolean;
  totalCourses: number;
  totalPositions: number;
  statusApproval: string;
  totalBudget?: number; // New: sum of course budgets
}

export interface ApprovalData {
  msLCMSPlanningID: string;
  status: 'Approved' | 'Rejected';
  notes: string;
}

export interface CourseStatusUpdateData {
  trLCMSPlanningCoursesID: string;
  status: 'canceled' | 'dropped';
  notes: string;
}

export interface CourseStatus {
  status: 'planned' | 'dropped' | 'canceled';
  updatedBy?: string;
  updatedDate?: string;
  notes?: string;
}

export interface StatusApproval {
  status: 'planned' | 'approved' | 'rejected';
  approvedBy?: string;
  approvedDate?: string;
  notes?: string;
}

export interface YearlyCalendarFilters {
  search?: string;
  programType?: string;
  startDate?: string;
  endDate?: string;
  sortBy?: string;
  statusApproval?: string;
  sortOrder?: 'asc' | 'desc';
}

export interface YearlyCalendarCourse {
  trLCMSPlanningCoursesID: string;
  msLCMSCourseID: string;
  courseName: string;
  courseCode: string;
  deliveryType: string;
  learningHours: number;
  planningStart: string;
  planningEnd: string;
  modeOfDelivery?: string;
  schoolName?: string;
  statusCourse?: string;
  budget?: number;
  isCEAReady?: boolean;
  listModule?: Array<{
    TrModuleID?: string;
    moduleType: string;
    moduleStart: string;
    moduleEnd: string;
  }>;
  historyTransaksi?: Array<{
    startDate: string;
    endDate: string;
  }>;
  facilitators?: Array<{
    name: string;
    email: string;
    type: string;
    binusianID: string;
    approvalLevel: string | null;
    linkURL: string;
  }>;
}

export interface YearlyCalendarTargetPosition {
  trLCMSPlanningTargetPositionsID: string;
  positionID: string;
  positionName: string;
}

export interface YearlyCalendarSchedulePlanDetail extends YearlyCalendarSchedulePlan {
  courses: YearlyCalendarCourse[];
  scheduleAssessmentStart?: string;
  scheduleAssessmentEnd?: string;
  targetPositions: YearlyCalendarTargetPosition[];
}

export interface UpdateSchedulePlanDto {
  msLCMSPlanningID: string;
  courses: Array<{
    courseID: string;
    planningStart: string;
    planningEnd: string;
    budget?: number;
    trLCMSPlanningCoursesID?: string; // Optional - null/empty for new batches, existing ID for updates
    isCEAReady?: boolean;
    reSchedule?: number; // 0 or 1
    modeOfDelivery?: string;
    listModule?: Array<{
      TrModuleID?: string;
      moduleType: string;
      moduleStart: string;
      moduleEnd: string;
    }>;
  }>;
  scheduleAssessmentStart?: string;
  scheduleAssessmentEnd?: string;
}

export interface EnhancedYearlyCalendarCourse extends YearlyCalendarCourse {
  instanceId?: string;
  hasConflict?: boolean;
  conflictWith?: string[];
  description?: string;
  category?: string;
}

export interface AvailableCourseData {
  msLCMSCourseID: string;
  courseName: string;
  courseCode: string;
  learningHours: number;
  deliveryType: string;
  description?: string;
  category?: string;
  courseCategory?: string;
  isActive: boolean;
}
export interface EnhancedUpdateSchedulePlanDto extends UpdateSchedulePlanDto {
  coursesToUpdate?: Array<{
    trLCMSPlanningCoursesID: string;
    courseID: string;
    planningStart: string;
    planningEnd: string;
    budget?: number; // New
  }>;
  coursesToDelete?: string[];
  coursesToAdd?: Array<{
    courseID: string;
    planningStart: string;
    planningEnd: string;
    budget?: number; // New
  }>;
}

export interface BulkCourseOperationDto {
  msLCMSPlanningID: string;
  operations: Array<{
    type: 'create' | 'update' | 'delete';
    trLCMSPlanningCoursesID?: string;
    courseID: string;
    planningStart?: string;
    planningEnd?: string;
    budget?: number; // New
  }>;
}

export interface CourseConflictInfo {
  planningId: string;
  conflicts: {
    courseInstanceId: string;
    courseName: string;
    conflictsWith: {
      courseInstanceId: string;
      courseName: string;
      overlapDays: number;
    }[];
  }[];
}

export interface CalendarViewData {
  msLCMSPlanningID: string;
  planningName: string;
  courses: {
    trLCMSPlanningCoursesID: string;
    msLCMSCourseID: string;
    courseName: string;
    courseCode: string;
    planningStart: string;
    planningEnd: string;
    learningHours: number;
    deliveryType: string;
    color?: string;
    hasConflict: boolean;
  }[];
}

export interface CourseStatistics {
  totalCourses: number;
  scheduledCourses: number;
  unscheduledCourses: number;
  conflictingCourses: number;
  totalLearningHours: number;
  averageLearningHours: number;
  planningDuration: number; // in days
  coursesByDeliveryType: {
    [key: string]: number;
  };
}

export interface ExportOptions {
  format: 'excel' | 'pdf' | 'csv';
  includeConflicts: boolean;
  includeStatistics: boolean;
  dateRange?: {
    start: string;
    end: string;
  };
}

export interface HolidayEvent {
  eventName: string;
  dateImpacted: string;
}

export interface HolidayCalendarResponse {
  pageNumber: number;
  pageSize: number;
  totalPages: number;
  totalRecords: number;
  message: string;
  data: HolidayEvent[];
}

class YearlyCalendarSchedulePlanService {
  private baseUrl = '/yearly-calendar/planning';

  async getSchedulePlanList(
    page: number,
    pageSize: number,
    filters: YearlyCalendarFilters
  ): Promise<ApiResponse<YearlyCalendarSchedulePlan>> {
    const params = {
      pageNumber: page,
      pageSize,
      search: filters.search || '',
      startDate: filters.startDate,
      endDate: filters.endDate,
      statusApproval: filters.statusApproval,
      sortBy: filters.sortBy,
      sortOrder: filters.sortOrder,
      programType: filters.programType
    };

    const response = await httpClient.get<ApiResponse<YearlyCalendarSchedulePlan>>(
      API_ENDPOINTS.YEARLY_CALENDAR.LIST,
      { params }
    );
    return response.data;
  }

  async deleteSchedulePlan(id: string): Promise<{ message: string }> {
    try {
      const response = await httpClient.delete(`${this.baseUrl}/delete`, {
        data: { msLCMSPlanningID: id }
      });
      return response.data;
    } catch {
      throw new Error('Failed to delete schedule plan');
    }
  }

  async deleteBatch(trLCMSPlanningCoursesID: string): Promise<{ message: string }> {
    try {
      const response = await httpClient.delete(
        `/lcms/planning/delete-batch/${trLCMSPlanningCoursesID}`
      );
      return response.data;
    } catch {
      throw new Error('Failed to delete batch');
    }
  }

  async deleteSession(TrModuleID: string): Promise<{ message: string }> {
    try {
      const response = await httpClient.delete(`/lcms/planning/delete-sessions/${TrModuleID}`);
      return response.data;
    } catch {
      throw new Error('Failed to delete session');
    }
  }

  async getSchedulePlanDetail(
    id: string
  ): Promise<{ message: string; data: YearlyCalendarSchedulePlanDetail }> {
    try {
      const response = await httpClient.get(`${this.baseUrl}/detail/${id}`);
      return response.data;
    } catch {
      throw new Error('Failed to fetch schedule plan detail');
    }
  }

  async updateSchedulePlan(data: UpdateSchedulePlanDto): Promise<{ message: string; data: any }> {
    try {
      const response = await httpClient.put(`${this.baseUrl}/update`, data);
      return response.data;
    } catch {
      throw new Error('Failed to update schedule plan');
    }
  }

  async approveSchedulePlan(approvalData: ApprovalData): Promise<any> {
    const response = await httpClient.post(
      API_ENDPOINTS.YEARLY_CALENDAR.APPROVE_PLANNING,
      approvalData
    );
    return response.data;
  }

  async getAvailableCourses(
    pageNumber: number = 1,
    pageSize: number = 100,
    search?: string,
    deliveryType?: string
  ): Promise<ApiResponse<AvailableCourseData>> {
    try {
      const params = new URLSearchParams({
        pageNumber: pageNumber.toString(),
        pageSize: pageSize.toString(),
        ...(search && { search }),
        ...(deliveryType && { deliveryType })
      });

      const response = await httpClient.get<ApiResponse<AvailableCourseData>>(
        `/lcms/courses/available?${params.toString()}`
      );

      return response.data;
    } catch {
      // Mock data for development
      return this.getMockAvailableCourses(pageNumber, pageSize, search, deliveryType);
    }
  }

  async updateSchedulePlanEnhanced(
    data: EnhancedUpdateSchedulePlanDto
  ): Promise<{ message: string; data: any }> {
    try {
      const response = await httpClient.put(`${this.baseUrl}/update-enhanced`, data);
      return response.data;
    } catch {
      // Fallback to regular update for backward compatibility
      return this.updateSchedulePlan(data);
    }
  }

  async updateScheduleCoursePlanEnhanced(data: any): Promise<{ message: string; data: any }> {
    try {
      const response = await httpClient.post(`${this.baseUrl}/updatestatus`, data);
      return response.data;
    } catch {
      return this.updateSchedulePlan(data);
    }
  }

  /**
   * Bulk course operations for multiple instances
   */
  async bulkCourseOperations(
    data: BulkCourseOperationDto
  ): Promise<{ message: string; data: any; statistics: any }> {
    try {
      const response = await httpClient.post(`${this.baseUrl}/bulk-operations`, data);
      return response.data;
    } catch {
      throw new Error('Failed to perform bulk operations');
    }
  }

  /**
   * Get conflict information for a schedule plan
   */
  async getConflictInfo(
    planningId: string
  ): Promise<{ message: string; data: CourseConflictInfo }> {
    try {
      const response = await httpClient.get(`${this.baseUrl}/conflicts/${planningId}`);
      return response.data;
    } catch {
      // Return empty conflicts for development
      return {
        message: 'No conflicts detected',
        data: {
          planningId,
          conflicts: []
        }
      };
    }
  }

  /**
   * Get calendar view data optimized for calendar display
   */
  async getCalendarViewData(
    planningId: string
  ): Promise<{ message: string; data: CalendarViewData }> {
    try {
      const response = await httpClient.get(`${this.baseUrl}/calendar-view/${planningId}`);
      return response.data;
    } catch {
      // Fallback to regular detail and transform
      const detail = await this.getSchedulePlanDetail(planningId);
      return {
        message: 'Success',
        data: {
          msLCMSPlanningID: detail.data.msLCMSPlanningID,
          planningName: detail.data.planningName,
          courses: detail.data.courses.map((course) => ({
            ...course,
            color: '#228be6',
            hasConflict: false
          }))
        }
      };
    }
  }

  /**
   * Get course statistics for a schedule plan
   */
  async getCourseStatistics(
    planningId: string
  ): Promise<{ message: string; data: CourseStatistics }> {
    try {
      const response = await httpClient.get(`${this.baseUrl}/statistics/${planningId}`);
      return response.data;
    } catch {
      // Calculate basic statistics from detail data
      const detail = await this.getSchedulePlanDetail(planningId);
      const courses = detail.data.courses;

      const statistics: CourseStatistics = {
        totalCourses: courses.length,
        scheduledCourses: courses.filter((c) => c.planningStart && c.planningEnd).length,
        unscheduledCourses: courses.filter((c) => !c.planningStart || !c.planningEnd).length,
        conflictingCourses: 0, // Would need conflict detection
        totalLearningHours: courses.reduce((sum, c) => sum + c.learningHours, 0),
        averageLearningHours:
          courses.length > 0
            ? courses.reduce((sum, c) => sum + c.learningHours, 0) / courses.length
            : 0,
        planningDuration: this.calculatePlanningDuration(
          detail.data.planningStart,
          detail.data.planningEnd
        ),
        coursesByDeliveryType: this.groupCoursesByDeliveryType(courses)
      };

      return {
        message: 'Statistics calculated',
        data: statistics
      };
    }
  }

  /**
   * Export schedule plan data
   */
  async exportSchedulePlan(
    planningId: string,
    options: ExportOptions
  ): Promise<{ message: string; downloadUrl: string }> {
    try {
      const response = await httpClient.post(`${this.baseUrl}/export/${planningId}`, options);
      return response.data;
    } catch {
      throw new Error('Failed to export schedule plan');
    }
  }

  /**
   * Duplicate a course instance within a schedule plan
   */
  async duplicateCourseInstance(
    planningId: string,
    courseInstanceId: string
  ): Promise<{ message: string; data: YearlyCalendarCourse }> {
    try {
      const response = await httpClient.post(`${this.baseUrl}/duplicate-course`, {
        msLCMSPlanningID: planningId,
        trLCMSPlanningCoursesID: courseInstanceId
      });
      return response.data;
    } catch {
      throw new Error('Failed to duplicate course instance');
    }
  }

  async getHolidayCalendar(year?: number): Promise<HolidayCalendarResponse> {
    try {
      const params = year ? { year } : {};
      const response = await httpClient.get<HolidayCalendarResponse>('/lcms/holiday-calendar', {
        params
      });
      return response.data;
    } catch {
      // Return empty holidays array if API fails
      return {
        pageNumber: 1,
        pageSize: 0,
        totalPages: 1,
        totalRecords: 0,
        message: 'No holidays found',
        data: []
      };
    }
  }

  /**
   * Validate schedule plan for conflicts and issues
   */
  async validateSchedulePlan(planningId: string): Promise<{
    message: string;
    data: {
      isValid: boolean;
      errors: any[];
      warnings: any[];
      conflicts: CourseConflictInfo;
    };
  }> {
    try {
      const response = await httpClient.get(`${this.baseUrl}/validate/${planningId}`);
      return response.data;
    } catch {
      // Basic validation using conflict info
      const conflicts = await this.getConflictInfo(planningId);
      return {
        message: 'Validation completed',
        data: {
          isValid: conflicts.data.conflicts.length === 0,
          errors: [],
          warnings:
            conflicts.data.conflicts.length > 0 ? [{ message: 'Schedule conflicts detected' }] : [],
          conflicts: conflicts.data
        }
      };
    }
  }

  private getMockAvailableCourses(
    pageNumber: number,
    pageSize: number,
    search?: string,
    deliveryType?: string
  ): ApiResponse<AvailableCourseData> {
    const mockCourses: AvailableCourseData[] = [
      {
        msLCMSCourseID: 'course-001',
        courseName: 'JavaScript Fundamentals',
        courseCode: 'JS101',
        learningHours: 40,
        deliveryType: 'modular',
        description: 'Learn the basics of JavaScript programming',
        category: 'Programming',
        isActive: true
      },
      {
        msLCMSCourseID: 'course-002',
        courseName: 'React Development',
        courseCode: 'REACT201',
        learningHours: 60,
        deliveryType: 'modular',
        description: 'Advanced React development techniques',
        category: 'Frontend',
        isActive: true
      },
      {
        msLCMSCourseID: 'course-003',
        courseName: 'Database Design',
        courseCode: 'DB301',
        learningHours: 35,
        deliveryType: 'program',
        description: 'Comprehensive database design principles',
        category: 'Backend',
        isActive: true
      },
      {
        msLCMSCourseID: 'course-004',
        courseName: 'Project Management',
        courseCode: 'PM101',
        learningHours: 25,
        deliveryType: 'modular',
        description: 'Essential project management skills',
        category: 'Management',
        isActive: true
      },
      {
        msLCMSCourseID: 'course-005',
        courseName: 'UI/UX Design',
        courseCode: 'UX201',
        learningHours: 45,
        deliveryType: 'program',
        description: 'User interface and experience design',
        category: 'Design',
        isActive: true
      }
    ];

    let filteredCourses = mockCourses;

    if (search) {
      filteredCourses = filteredCourses.filter(
        (course) =>
          course.courseName.toLowerCase().includes(search.toLowerCase()) ||
          course.courseCode.toLowerCase().includes(search.toLowerCase()) ||
          course.description?.toLowerCase().includes(search.toLowerCase())
      );
    }

    if (deliveryType) {
      filteredCourses = filteredCourses.filter((course) => course.deliveryType === deliveryType);
    }

    const startIndex = (pageNumber - 1) * pageSize;
    const paginatedData = filteredCourses.slice(startIndex, startIndex + pageSize);

    return {
      pageNumber,
      pageSize,
      totalPages: Math.ceil(filteredCourses.length / pageSize),
      totalRecords: filteredCourses.length,
      message: 'Success',
      data: paginatedData
    };
  }

  private calculatePlanningDuration(startDate: string, endDate: string): number {
    const start = new Date(startDate);
    const end = new Date(endDate);
    const diffTime = Math.abs(end.getTime() - start.getTime());
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  }

  private groupCoursesByDeliveryType(courses: YearlyCalendarCourse[]): { [key: string]: number } {
    return courses.reduce(
      (acc, course) => {
        acc[course.deliveryType] = (acc[course.deliveryType] || 0) + 1;
        return acc;
      },
      {} as { [key: string]: number }
    );
  }
}

export const yearlyCalendarSchedulePlanService = new YearlyCalendarSchedulePlanService();
